import { Collapse, FilterChipGroup, Grid, InputLabel, Paper, useClientOptions, makeStyles } from '@paintscout/ui'
import type { ValueFilter } from 'json-to-lucene'
import React from 'react'
import classnames from 'classnames'
import { formatCurrency } from '@paintscout/util/builder'
import moment from 'moment'

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%'
  },
  paper: {
    padding: theme.spacing(4, 6),
    marginBottom: theme.spacing(1)
  },
  chipGroup: {
    margin: theme.spacing(1, 0),
    gridTemplateColumns: `repeat(auto-fill, minmax(120px, 1fr))`
  },
  longChip: {
    gridTemplateColumns: `1fr`
  }
}))

interface LocationSearchFilterChipsProps {
  show: boolean
  filters: ValueFilter[]
  onDelete: (filter: ValueFilter, valueIndex: number) => void
}

export default function LocationSearchFilterChips(props: LocationSearchFilterChipsProps) {
  const { show, filters, onDelete } = props
  const classes = useStyles()
  const { options } = useClientOptions()

  const dateFormat = options.options.dateFormat.momentValue

  const dateFilter = filters.find((filter) => filter.id === 'created')
  const dollarsWonFilter = filters.find((filter) => filter.id === 'aggregates.closedValue')
  const userCountFilter = filters.find((filter) => filter.id === 'userCounts.all')
  const quoteCountFilter = filters.find((filter) => filter.id === 'aggregates.totalQuotes')
  const winRateFilter = filters.find((filter) => filter.id === 'aggregates.closeRate')

  const getDateFilterChipValue = () => {
    if (!dateFilter?.value) {
      return ''
    }
    if (dateFilter.value[0] && !dateFilter.value[1]) {
      return `After ${moment(dateFilter.value[0]).format(dateFormat)}`
    }
    if (!dateFilter.value[0] && dateFilter.value[1]) {
      return `Before ${moment(dateFilter.value[1]).format(dateFormat)}`
    }
    return `${moment(dateFilter.value[0]).format(dateFormat)} - ${moment(dateFilter.value[1]).format(dateFormat)}`
  }

  const getDollarValueFilterChipLabel = (filter: ValueFilter) => {
    if (!filter?.value) {
      return ''
    }
    if (filter.value[0] && !filter.value[1]) {
      return `Over ${formatCurrency({ value: filter.value[0], options })}`
    }
    if (!filter.value[0] && filter.value[1]) {
      return `Under ${formatCurrency({ value: filter.value[1], options })}`
    }
    return `${formatCurrency({ value: filter.value[0], options })} - ${formatCurrency({ value: filter.value[1], options })}`
  }

  const getValueFilterChipLabel = (filter: ValueFilter) => {
    if (!filter?.value) {
      return ''
    }
    if (filter.value[0] && !filter.value[1]) {
      return `Over ${filter.value[0]}`
    }
    if (!filter.value[0] && filter.value[1]) {
      return `Under ${filter.value[1]}`
    }
    return `${filter.value[0]} - ${filter.value[1]}`
  }

  return (
    <div className={classes.root}>
      <Collapse show={show}>
        <Paper className={classnames(classes.root, classes.paper)}>
          <Grid container spacing={5}>
            {!!userCountFilter?.value && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>User Count</InputLabel>
                <FilterChipGroup
                  classes={{ root: classnames(classes.chipGroup, classes.longChip) }}
                  chips={[
                    {
                      label: getValueFilterChipLabel(userCountFilter),
                      onDelete: () => onDelete(userCountFilter, 0)
                    }
                  ]}
                />
              </Grid>
            )}
            {!!dateFilter?.value && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>Created Date</InputLabel>
                <FilterChipGroup
                  classes={{ root: classnames(classes.chipGroup, classes.longChip) }}
                  chips={[
                    {
                      label: getDateFilterChipValue(),
                      onDelete: () => onDelete(dateFilter, 0)
                    }
                  ]}
                />
              </Grid>
            )}
            {!!quoteCountFilter?.value && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>Quote Count</InputLabel>
                <FilterChipGroup
                  classes={{ root: classnames(classes.chipGroup, classes.longChip) }}
                  chips={[
                    {
                      label: getValueFilterChipLabel(quoteCountFilter),
                      onDelete: () => onDelete(quoteCountFilter, 0)
                    }
                  ]}
                />
              </Grid>
            )}
            {!!dollarsWonFilter?.value && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>Dollars Won ($)</InputLabel>
                <FilterChipGroup
                  classes={{ root: classnames(classes.chipGroup, classes.longChip) }}
                  chips={[
                    {
                      label: getDollarValueFilterChipLabel(dollarsWonFilter),
                      onDelete: () => onDelete(dollarsWonFilter, 0)
                    }
                  ]}
                />
              </Grid>
            )}
            {!!winRateFilter?.value && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>Win Rate (%)</InputLabel>
                <FilterChipGroup
                  classes={{ root: classnames(classes.chipGroup, classes.longChip) }}
                  chips={[
                    {
                      label: `${getValueFilterChipLabel(winRateFilter)}%`,
                      onDelete: () => onDelete(winRateFilter, 0)
                    }
                  ]}
                />
              </Grid>
            )}
          </Grid>
        </Paper>
      </Collapse>
    </div>
  )
}
